<script>
  import { onMount } from 'svelte';
  import {
    COLORS, PADDING, buildScales, clearCanvas, drawGrid, drawTimeLabels, indexFromX,
  } from './chartUtils.js';
  import { formatSignedPercent } from '../../../lib/strategyResult.js';

  // Cumulative-vs-benchmark: { strategy: [{time, value}], benchmark: [{time, value}] | null } (fractions,
  // rebased to 0). Server-computed (KTD4). The benchmark line is absent when benchmark is null (KTD7).
  export let data = { strategy: [], benchmark: null };

  let canvas;
  let ctx;
  let container;
  let width = 600;
  let height = 200;
  let hoverIndex = -1;

  const fmt = (v) => formatSignedPercent(v);
  $: strategy = (data && data.strategy) || [];
  $: benchmark = (data && data.benchmark) || null;
  $: hasData = strategy.length >= 2;
  $: summary = hasData
    ? `Cumulative return vs benchmark: strategy ends ${fmt(strategy[strategy.length - 1].value)}` +
      (benchmark && benchmark.length ? `, benchmark ends ${fmt(benchmark[benchmark.length - 1].value)}` : ', no benchmark') +
      `, ${strategy.length} points.`
    : 'Cumulative return vs benchmark: insufficient data.';

  function resize() {
    if (!container) return;
    width = Math.max(200, container.offsetWidth);
    height = 200;
  }

  function valueRange() {
    let min = Infinity;
    let max = -Infinity;
    const scan = (arr) => { for (const p of arr) { if (p.value < min) min = p.value; if (p.value > max) max = p.value; } };
    scan(strategy);
    if (benchmark && benchmark.length) scan(benchmark);
    if (!Number.isFinite(min) || !Number.isFinite(max)) { min = 0; max = 1; }
    return { min, max };
  }

  function line(arr, scales, color) {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    arr.forEach((p, i) => {
      const x = scales.xOf(i);
      const y = scales.yOf(p.value);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();
  }

  function draw() {
    if (!ctx || !hasData) return;
    clearCanvas(ctx, width, height);
    const { min, max } = valueRange();
    // Strategy drives the x-index/time axis; both series share the same aligned timeline (U6).
    const scales = buildScales(strategy, width, height, { valueMin: min, valueMax: max });
    drawGrid(ctx, width, height, scales, fmt);
    drawTimeLabels(ctx, strategy, height, scales);

    if (benchmark && benchmark.length) line(benchmark, scales, COLORS.label);
    line(strategy, scales, COLORS.line);

    // Legend.
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillStyle = COLORS.line;
    ctx.fillText('â  Strategy', PADDING.left + 4, PADDING.top + 2);
    if (benchmark && benchmark.length) {
      ctx.fillStyle = COLORS.label;
      ctx.fillText('â  Benchmark', PADDING.left + 70, PADDING.top + 2);
    }

    if (hoverIndex >= 0 && hoverIndex < strategy.length) {
      const x = scales.xOf(hoverIndex);
      ctx.strokeStyle = COLORS.crosshair;
      ctx.globalAlpha = 0.4;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(x, PADDING.top);
      ctx.lineTo(x, height - PADDING.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }
  }

  $: if (ctx && canvas && hasData && width && height && strategy && benchmark !== undefined && hoverIndex >= -1) draw();

  function onMove(e) {
    if (!hasData) return;
    const rect = canvas.getBoundingClientRect();
    hoverIndex = indexFromX(e.clientX - rect.left, strategy.length, width);
  }
  function onLeave() { hoverIndex = -1; }

  let liveMsg = '';
  function onKey(e) {
    if (!hasData) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const start = hoverIndex < 0 ? 0 : hoverIndex;
      hoverIndex = Math.max(0, Math.min(strategy.length - 1, start + (e.key === 'ArrowRight' ? 1 : -1)));
      const sp = strategy[hoverIndex];
      const bp = benchmark && benchmark[hoverIndex];
      liveMsg = `${new Date(sp.time).toLocaleDateString()}: strategy ${fmt(sp.value)}` +
        (bp ? `, benchmark ${fmt(bp.value)}` : '');
    }
  }

  // Lazy 2D-context acquisition: the canvas renders inside {#if hasData}, so it may not exist at
  // mount (empty data — acquiring unconditionally threw and broke the whole reactive graph) and may
  // APPEAR later when data arrives. Acquire the context whenever the canvas binds.
  $: if (canvas && !ctx) { ctx = canvas.getContext('2d'); resize(); }

  onMount(() => {
    if (!canvas) return; // not rendered (empty state) — nothing to wire up
    if (canvas) ctx = canvas.getContext('2d');
    resize();
    draw();
    const onResize = () => { resize(); draw(); };
    window.addEventListener('resize', onResize);
    let pendingDraw;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && ctx && canvas) {
          clearTimeout(pendingDraw);
          pendingDraw = setTimeout(() => { resize(); draw(); }, 50);
        }
      });
    }, { threshold: 0.1 });
    if (container) observer.observe(container);
    return () => { window.removeEventListener('resize', onResize); observer.disconnect(); clearTimeout(pendingDraw); };
  });
</script>

<div class="bt-chart" bind:this={container}>
  <h4 class="bt-chart-title">Cumulative Return vs Benchmark</h4>
  {#if hasData}
    <canvas
      bind:this={canvas}
      width={width}
      height={height}
      tabindex="0"
      aria-label={summary}
      on:mousemove={onMove}
      on:mouseleave={onLeave}
      on:keydown={onKey}
    ></canvas>
    <div class="bt-chart-live" aria-live="polite">{liveMsg}</div>
    {#if !benchmark || !benchmark.length}
      <p class="bt-chart-note">No benchmark selected â showing the strategy only.</p>
    {/if}
    <details class="bt-chart-data">
      <summary>View data as table</summary>
      <table>
        <thead><tr><th>Time</th><th>Strategy</th>{#if benchmark && benchmark.length}<th>Benchmark</th>{/if}</tr></thead>
        <tbody>
          {#each strategy as p, i}
            <tr>
              <td>{new Date(p.time).toLocaleDateString()}</td>
              <td>{fmt(p.value)}</td>
              {#if benchmark && benchmark.length}<td>{fmt(benchmark[i]?.value)}</td>{/if}
            </tr>
          {/each}
        </tbody>
      </table>
    </details>
  {:else}
    <div class="empty-state"><p class="empty-state-description">Insufficient data for this chart.</p></div>
  {/if}
</div>

<style>
  @import './charts.css';
  .bt-chart-note {
    margin: 0.25rem 0 0;
    font-size: 0.75rem;
    color: var(--text-muted, #8a8b8d);
  }
</style>
